/**
 * IA Cart Upsell - Re-renders when first cart item's product ID changes
 */

import { EVENTS, subscribe } from "ia-util-pubsub";

// API handler for fetching product recommendations
class RecommendationsAPI {
  static async fetch(productId, { limit, intent, sectionId }) {
    if (!productId) return [];
    
    try {
      const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=${limit}&intent=${intent}&section_id=${sectionId}`;
      const response = await fetch(url);
      if (!response.ok) return [];

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const slider = doc.querySelector('ia-slider');
      return slider ? Array.from(slider.querySelectorAll('.ia-cart-upsell__item-wrapper')) : [];
    } catch (error) {
      console.error('Error loading recommendations:', error);
      return [];
    }
  }
}

if (!customElements.get('ia-cart-upsell')) {

  class IaCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.baseProductId = this.dataset.baseProductId || '';
      this.cartProductIds = [];
      this.enableDynamic = this.dataset.enableDynamic === 'true';
      this.sliderTemplate = null;
    }

    connectedCallback() {
      this.cartProductIds = this.getUniqueIds((this.dataset.cartProductIds || '').split(',').filter(id => id.trim()));
      
      // Save the original slider template
      const slider = this.querySelector('ia-slider');
      if (slider) {
        this.sliderTemplate = slider.outerHTML;
      }

      this.render();
      this.unsubscribe = subscribe(EVENTS.cartChanged, this.handleCartChanged.bind(this));
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    getUniqueIds(ids) {
      return [...new Set(ids.map(id => String(id)))];
    }

    arraysEqual(arr1, arr2) {
      if (arr1.length !== arr2.length) return false;
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();
      return sorted1.every((val, index) => val === sorted2[index]);
    }

    handleCartChanged(event) {
      const { cartObj, cartDom } = event.detail;
      if (!cartObj) return;

      const newCartProductIds = this.getUniqueIds(cartObj.items?.map(item => String(item.product_id)) || []);
      const newBaseProductId = cartObj.items?.[0]?.product_id ? String(cartObj.items[0].product_id) : '';

      // Check if cart products actually changed
      const cartChanged = !this.arraysEqual(this.cartProductIds, newCartProductIds);
      const baseProductChanged = newBaseProductId !== this.baseProductId;

      if (!cartChanged && !baseProductChanged) {
        return; // Nothing changed, skip render
      }

      this.cartProductIds = newCartProductIds;
      this.baseProductId = newBaseProductId;
      this.render(cartDom);
    }

    async render(cartDom = null) {
      const preSelectedItems = this.getPreSelectedItems(cartDom);
      const apiItems = this.enableDynamic && this.baseProductId 
        ? await RecommendationsAPI.fetch(this.baseProductId, {
            limit: parseInt(this.dataset.limit) || 10,
            intent: this.dataset.intent || 'complementary',
            sectionId: this.dataset.sectionId
          })
        : [];

      this.rebuildSlider([...apiItems, ...preSelectedItems]);
    }

    getPreSelectedItems(cartDom) {
      const source = cartDom?.querySelector('ia-cart-upsell') || this;
      return Array.from(source.querySelectorAll('.ia-cart-upsell__item-wrapper'));
    }

    rebuildSlider(items) {
      const filtered = items.filter(item => {
        const productId = item.dataset.productId;
        return productId && !this.cartProductIds.includes(String(productId));
      });

      if (filtered.length === 0) {
        this.style.display = 'none';
        return;
      }

      this.style.display = '';

      // Create temporary container to parse template
      const temp = document.createElement('div');
      temp.innerHTML = this.sliderTemplate;
      const newSlider = temp.querySelector('ia-slider');
      
      // Get the slides container and replace its content
      const slidesContainer = newSlider.querySelector('.ia-slider__slides');
      if (slidesContainer) {
        slidesContainer.innerHTML = '';
        filtered.forEach(item => slidesContainer.appendChild(item.cloneNode(true)));
      }

      // Replace the entire slider
      const oldSlider = this.querySelector('ia-slider');
      if (oldSlider) {
        oldSlider.replaceWith(newSlider);
      } else {
        this.innerHTML = temp.innerHTML;
      }
    }
  }

  customElements.define('ia-cart-upsell', IaCartUpsell);
}
